import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Loader2, Trash2, Palette, X, Heart } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead } from "@/components/Reveal";
import { API, http, safeGet } from "@/lib/api";
import { resizeImage } from "@/lib/images";
import { NEEDS_CAPTCHA, Turnstile } from "@/components/Turnstile";

const FRAMES = ["#ff4d8d", "#38b6ff", "#2fb344", "#ff7a2f", "#7c4dff", "#ffd23f"];
export const artSrc = (id, size = "thumb") => `${API}/gallery/${id}/image?size=${size}`;

const HEARTED_KEY = "ananawi_hearts";
const readHearted = () => {
  try {
    return JSON.parse(localStorage.getItem(HEARTED_KEY) || "[]");
  } catch {
    return [];
  }
};

const HeartButton = ({ a, t }) => {
  const [count, setCount] = useState(a.hearts ?? 0);
  const [liked, setLiked] = useState(() => readHearted().includes(a.id));
  const [pop, setPop] = useState(0);
  const send = async (e) => {
    e.stopPropagation();
    setPop(Date.now());
    window.dispatchEvent(new Event("ananawi-applause"));
    if (!liked) {
      setLiked(true);
      localStorage.setItem(HEARTED_KEY, JSON.stringify([...readHearted(), a.id].slice(-300)));
    }
    try {
      const { data } = await http.post(`/gallery/${a.id}/heart`);
      if (typeof data?.hearts === "number") setCount(data.hearts);
    } catch {}
  };
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={send}
      onKeyDown={(e) => { if (e.key === "Enter") send(e); }}
      aria-label={t.gallery.heart}
      className="relative inline-flex items-center gap-1 rounded-full bg-white border-2 border-ink px-2.5 py-1 text-sm font-extrabold transition-transform duration-200 hover:scale-110"
      data-testid={`gallery-heart-${a.id}`}
    >
      <motion.span key={pop} initial={{ scale: 1 }} animate={{ scale: pop ? [1, 1.6, 1] : 1 }} transition={{ duration: 0.4 }} className="inline-flex">
        <Heart className={`w-4 h-4 ${liked ? "fill-pink text-pink" : "text-ink"}`} />
      </motion.span>
      <span data-testid={`gallery-heart-count-${a.id}`}>{count}</span>
    </span>
  );
};

const ArtCard = ({ a, i, onOpen, t }) => (
  <motion.button
    type="button"
    onClick={() => onOpen(a)}
    initial={{ opacity: 0, y: 30, rotate: 0 }}
    whileInView={{ opacity: 1, y: 0, rotate: i % 2 ? 2 : -2 }}
    viewport={{ once: true, amount: 0.1 }}
    whileHover={{ rotate: 0, scale: 1.04 }}
    transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
    className="text-left rounded-[20px] p-2.5 pb-3 sticker bg-white"
    style={{ borderColor: "#1f1a3d" }}
    data-testid={`gallery-card-${a.id}`}
  >
    <div className="relative aspect-square overflow-hidden rounded-[14px] border-[3px]" style={{ borderColor: FRAMES[i % FRAMES.length] }}>
      <img src={artSrc(a.id)} alt={a.title || a.child_name || "Ananawi"} loading="lazy" className="w-full h-full object-cover" />
      <span className="absolute bottom-2 right-2"><HeartButton a={a} t={t} /></span>
    </div>
    <p className="mt-2 px-1 font-display font-semibold leading-tight truncate">{a.title || "\u00a0"}</p>
    <p className="px-1 text-xs font-extrabold text-ink/70 truncate">{[a.child_name, a.age && `${a.age} ${t.gallery.age}`].filter(Boolean).join(" · ") || "\u00a0"}</p>
  </motion.button>
);

const Lightbox = ({ art, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[75] bg-ink/80 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="gallery-lightbox" data-lenis-prevent>
    <div className="relative max-w-3xl w-full rounded-[26px] sticker bg-white p-3" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute -top-4 -right-4 grid place-items-center w-11 h-11 rounded-full bg-sun sticker-sm" aria-label="close" data-testid="gallery-lightbox-close"><X className="w-5 h-5" /></button>
      <img src={artSrc(art.id, "full")} alt={art.title || ""} className="w-full max-h-[75vh] object-contain rounded-[18px]" />
      <p className="mt-3 px-2 font-display font-bold text-xl">{art.title} <span className="text-ink/70 text-base">{art.child_name} {art.age && `· ${art.age}`}</span></p>
    </div>
  </motion.div>
);

const Rosette = ({ rank }) => (
  <svg viewBox="0 0 64 80" className="w-14 h-[70px] drop-shadow-[2px_3px_0_#1f1a3d]" aria-hidden="true">
    <path d="M18 44 L10 78 L22 70 L30 80 L32 46 Z" fill={rank === 1 ? "#ff4d8d" : "#38b6ff"} stroke="#1f1a3d" strokeWidth="2.5" />
    <path d="M46 44 L54 78 L42 70 L34 80 L32 46 Z" fill={rank === 1 ? "#ff4d8d" : "#38b6ff"} stroke="#1f1a3d" strokeWidth="2.5" />
    {Array.from({ length: 12 }).map((_, i) => (
      <circle key={i} cx={32 + 21 * Math.cos((i / 12) * Math.PI * 2)} cy={30 + 21 * Math.sin((i / 12) * Math.PI * 2)} r="7" fill="#f5b800" stroke="#1f1a3d" strokeWidth="2.5" />
    ))}
    <circle cx="32" cy="30" r="20" fill="#ffd23f" stroke="#1f1a3d" strokeWidth="2.5" />
    <text x="32" y="38" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="22" fill="#1f1a3d">{rank}</text>
  </svg>
);

const TopHearts = ({ onOpen, t }) => {
  const [top, setTop] = useState([]);
  useEffect(() => {
    safeGet("/gallery/top", []).then((d) => setTop(Array.isArray(d) ? d : []));
  }, []);
  if (!top.length) return null;
  return (
    <div className="mt-12 rounded-[32px] sticker bg-sun p-5 sm:p-8" data-testid="gallery-top">
      <h3 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2"><Heart className="w-7 h-7 fill-pink text-pink" /> {t.gallery.topTitle}</h3>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 items-end">
        {top.map((a, i) => (
          <motion.button
            key={a.id}
            type="button"
            onClick={() => onOpen(a)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: i * 0.12 }}
            whileHover={{ y: -6, rotate: i === 1 ? 0 : i ? 2 : -2 }}
            className={`relative text-left rounded-[22px] sticker bg-white p-3 ${i === 0 ? "sm:order-2 sm:scale-105" : i === 1 ? "sm:order-1" : "sm:order-3"}`}
            data-testid={`gallery-top-${i + 1}`}
          >
            <span className="absolute -top-6 -left-3 z-10"><Rosette rank={i + 1} /></span>
            <img src={artSrc(a.id)} alt={a.title || ""} className="aspect-square w-full object-cover rounded-[14px] border-2 border-ink/20" />
            <p className="mt-2 font-display font-semibold truncate">{a.title || a.child_name || "Ananawi"}</p>
            <p className="text-sm font-extrabold text-pink flex items-center gap-1"><Heart className="w-4 h-4 fill-pink" /> {a.week_hearts}</p>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export const GallerySection = () => {
  const { t } = useI18n();
  const { gallery, user } = useSite();
  const [open, setOpen] = useState(null);
  const list = Array.isArray(gallery) ? gallery : [];
  return (
    <section id="galleria" className="relative overflow-hidden bg-[#fff0f6] py-24 sm:py-32" data-testid="gallery-section">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHead eyebrow={t.gallery.eyebrow} title={t.gallery.title} sub={t.gallery.sub} testid="gallery-section-head" eyebrowClass="bg-grape !text-white" />
          <Link to={user ? "/genitori#invia" : "/login?next=/genitori"} className="btn-pop btn-breathe sticker inline-flex items-center gap-2 rounded-full bg-pink text-white px-6 py-3.5 font-display font-semibold text-lg self-start md:self-end" data-testid="gallery-send-btn">
            <Palette className="w-5 h-5" /> {t.gallery.send}
          </Link>
        </div>
        <TopHearts onOpen={setOpen} t={t} />
        {list.length === 0 ? (
          <div className="mt-12 flex items-center gap-4" data-testid="gallery-empty">
            <img src="/brand/f_think.webp" alt={t.imgAlt.mascot} className="w-20" />
            <p className="font-display font-semibold text-xl">{t.gallery.empty}</p>
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 sm:gap-7" data-testid="gallery-grid">
            {list.map((a, i) => <ArtCard key={a.id} a={a} i={i} onOpen={setOpen} t={t} />)}
          </div>
        )}
      </div>
      {open && <Lightbox art={open} onClose={() => setOpen(null)} />}
    </section>
  );
};

const STATUS_CLS = { pending: "bg-sun text-ink", approved: "bg-leaf text-white", rejected: "bg-pink text-white" };
const inputCls = "mt-1 w-full rounded-2xl border-[2.5px] border-ink bg-white px-4 py-2.5 font-semibold focus:outline-none focus:ring-4 focus:ring-pink/40";

export const GalleryUpload = () => {
  const { t, lang } = useI18n();
  const g = t.gallery;
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ child_name: "", age: "", title: "" });
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/gallery/mine");
      setMine(Array.isArray(data) ? data : []);
    } catch {
      setMine([]);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    if (NEEDS_CAPTCHA && !captcha) return toast.error(t.common.error);
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("consent", consent ? "true" : "false");
    fd.append("lang", lang);
    if (NEEDS_CAPTCHA) fd.append("turnstile_token", captcha);
    try {
      // Resized + re-encoded in the browser (removes EXIF/GPS) before upload.
      const [full, thumb] = await Promise.all([resizeImage(file, 1600, 0.85), resizeImage(file, 600, 0.8)]);
      const ext = full.type === "image/webp" ? "webp" : "jpg";
      fd.append("file", full, `drawing.${ext}`);
      fd.append("thumb", thumb, `thumb.${ext}`);
      await http.post("/gallery", fd);
      toast.success(g.sentOk);
      setForm({ child_name: "", age: "", title: "" });
      setFile(null);
      setConsent(false);
      e.target.reset();
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(g.errors[d] ?? t.common.error);
    } finally {
      setBusy(false);
      setCaptcha("");
      setCaptchaKey((k) => k + 1);
    }
  };

  const remove = async (id) => {
    await http.delete(`/gallery/${id}`);
    load();
  };

  return (
    <div id="invia" className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="gallery-upload-area">
      <form onSubmit={submit} className="lg:col-span-2 rounded-[28px] sticker bg-white p-6 space-y-4" data-testid="gallery-upload-form">
        <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2"><Palette className="w-7 h-7 text-pink" /> {g.formTitle}</h2>
        <label className="block"><span className="text-sm font-extrabold">{g.file}</span>
          <input type="file" required accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className={inputCls} data-testid="gallery-file-input" />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block col-span-2"><span className="text-sm font-extrabold">{g.childName}</span>
            <input maxLength={20} value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} className={inputCls} data-testid="gallery-child-input" />
          </label>
          <label className="block"><span className="text-sm font-extrabold">{g.ageLabel}</span>
            <input maxLength={3} inputMode="numeric" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value.replace(/\D/g, "") })} className={inputCls} data-testid="gallery-age-input" />
          </label>
        </div>
        <label className="block"><span className="text-sm font-extrabold">{g.titleLabel}</span>
          <input maxLength={60} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="gallery-title-input" />
        </label>
        <label className="flex gap-3 items-start text-sm font-bold leading-relaxed cursor-pointer">
          <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-5 h-5 accent-pink shrink-0" data-testid="gallery-consent-checkbox" />
          {g.consent}
        </label>
        <Turnstile key={captchaKey} onToken={setCaptcha} testId="gallery-turnstile" />
        <button disabled={busy || (NEEDS_CAPTCHA && !captcha)} className="btn-pop sticker-sm btn-pop-sm w-full inline-flex justify-center items-center gap-2 rounded-full bg-pink text-white px-5 py-3.5 font-display font-semibold text-lg disabled:opacity-60" data-testid="gallery-submit-btn">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />} {g.submit}
        </button>
        <p className="text-center text-sm text-ink/70 font-medium leading-relaxed" data-testid="gallery-review-note">{t.gallery.reviewNote}</p>
      </form>
      <div className="lg:col-span-3">
        <h2 className="font-display font-bold text-2xl sm:text-3xl">{g.mine}</h2>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="gallery-mine-list">
          {mine.map((a) => (
            <div key={a.id} className="rounded-[18px] sticker-sm bg-white p-2" data-testid={`gallery-mine-${a.id}`}>
              <img src={artSrc(a.id)} alt="" className="aspect-square w-full object-cover rounded-xl" />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-extrabold ${STATUS_CLS[a.status]}`} data-testid={`gallery-mine-status-${a.id}`}>{g.status[a.status]}</span>
                <button onClick={() => remove(a.id)} className="p-1.5 rounded-full hover:bg-pink/15 text-pink" aria-label={g.remove} data-testid={`gallery-mine-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
